import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Bell, BellOff, X, CheckCircle2, Info } from 'lucide-react'

interface NotificationPermissionPromptProps {
  open: boolean
  onAllow: () => void
  onDeny: () => void
  onClose: () => void
}

export const NotificationPermissionPrompt: React.FC<NotificationPermissionPromptProps> = ({
  open,
  onAllow,
  onDeny,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-full">
              <Bell className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-xl">Activer les notifications</DialogTitle>
          </div>
          <DialogDescription className="pt-2 text-base">
            Restez informé en temps réel de vos transactions, mises à jour et offres exclusives
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30">
              <CheckCircle2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Alertes de transaction</p>
                <p className="text-sm text-muted-foreground">
                  Recevez des notifications instantanées pour vos dépôts et retraits
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/30">
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Offres promotionnelles</p>
                <p className="text-sm text-muted-foreground">
                  Profitez de bonus et offres exclusives réservés aux membres
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-950/30">
              <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">Mises à jour système</p>
                <p className="text-sm text-muted-foreground">
                  Soyez notifié des nouvelles fonctionnalités et améliorations
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              Vous pouvez désactiver les notifications à tout moment depuis les paramètres de votre navigateur ou de l'application.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button
            onClick={onAllow}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            size="lg"
          >
            <Bell className="mr-2 h-4 w-4" />
            Activer les notifications
          </Button>
          <Button
            onClick={onDeny}
            variant="ghost"
            className="w-full"
            size="lg"
          >
            <BellOff className="mr-2 h-4 w-4" />
            Non, merci
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface UseNotificationPromptResult {
  showPrompt: boolean
  handleAllow: () => Promise<void>
  handleDeny: () => void
  handleClose: () => void
}

export const useNotificationPrompt = (
  vapidKey: string,
  onPermissionGranted?: (token: string) => void
): UseNotificationPromptResult => {
  const [showPrompt, setShowPrompt] = useState(false)

  useEffect(() => {
    // Check if we should show the prompt
    const checkPermissionStatus = async () => {
      const permission = localStorage.getItem('notification_permission')
      const promptShown = localStorage.getItem('permission_prompt_shown')

      // Don't show if already decided or already shown
      if (permission && permission !== 'default') {
        return
      }

      if (promptShown) {
        return
      }

      // Show prompt after a short delay
      setTimeout(() => {
        setShowPrompt(true)
      }, 3000)
    }

    checkPermissionStatus()
  }, [])

  const handleAllow = async () => {
    try {
      const { requestNotificationPermission, subscribeToPushNotifications } = await import(
        '@/services/notificationService'
      )

      // Request permission
      const permission = await requestNotificationPermission()

      if (permission === 'granted') {
        // Subscribe to push notifications
        const token = await subscribeToPushNotifications(vapidKey)

        if (token && onPermissionGranted) {
          onPermissionGranted(token)
        }
      }

      setShowPrompt(false)
    } catch (error) {
      console.error('Error granting notification permission:', error)
    }
  }

  const handleDeny = () => {
    localStorage.setItem('notification_permission', 'denied')
    setShowPrompt(false)
  }

  const handleClose = () => {
    // Mark as shown but don't set permission
    const { markPermissionPromptShown } = require('@/services/notificationService')
    markPermissionPromptShown()
    setShowPrompt(false)
  }

  return {
    showPrompt,
    handleAllow,
    handleDeny,
    handleClose
  }
}
